import React, {
  Dispatch, ReactElement, SetStateAction, useEffect, useState,
} from 'react';
import {
  View, StyleSheet, Pressable, Text, ActivityIndicator,
  Modal,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import Colors from '../../../constants/Colors';
import Style from '../../../constants/Style';
import Button from '../../Button';
import BinIcon24 from '../../24pxIcons/BinIcon24';
import CloseModalIcon from '../../CloseModalIcon';
import { Floor } from './FloorItem';
import FloorList from './FloorList';
import AlertModal from '../../AlertModal';
import updateFloor from '../../../requests/Locations/Floors/UpdateFloor';
import FloorDetailsForm, { maxFloorNameLength, maxFloorNumberLength } from './FloorDetailsForm';
import deleteFloor from '../../../requests/Locations/Floors/DeleteFloor';
import updateFloorOrder from '../../../requests/Locations/Floors/UpdateFloorOrder';
import isMobile from '../../IsMobile';
import nameRegex, { externalIdRegex } from '../../Regex';
import { DELETE_FLOOR_FAILURE, FLOOR_PLAN_UPDATE_FAILURE } from '../../../constants/AlertMessages';
import { updateLocations } from '../../../redux/locationStructureSlice';
import { LocationType, UserPermission } from '../../../constants/Types';
import PressableStyled from '../../PressableStyled';
import { getFileNameFromPath } from '../../../constants/Utils';

interface SetFloorsModalProps {
  isVisible: boolean,
  hide: () => void,
  ParentId: string,
  floors: Floor[],
  setFloors?: Dispatch<SetStateAction<Floor[]>>,
  addFloor?,
  floorToUpdate?: Floor,
  editFloor?,
  deleteFloorToUpdate?,
  testID?: string,
}

const SetFloorsModal = ({
  isVisible, hide, ParentId, floors, setFloors, floorToUpdate, editFloor, addFloor,
  deleteFloorToUpdate, testID,
}: SetFloorsModalProps) => {
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canDeleteLocations: boolean = isAdmin
    || permissions.includes(UserPermission.LOCATIONS_DELETE);
  const [floorData, setFloorData] = useState<Floor[]>(floors);
  const [visibilityToggleEnabled, setVisibilityToggleEnabled] = useState<boolean>(true);
  const [selectedFloor, setSelectedFloor] = useState<Floor | null>(null);
  const [selectedFloorId, setSelectedFloorId] = useState<string | null>(null);
  const [currentFloorName, setCurrentFloorName] = useState<string | null>(null);
  const [currentFloorNumber, setCurrentFloorNumber] = useState<string | null>(null);
  const [currentFloorExternalId, setCurrentFloorExternalId] = useState<string | null>(null);
  const [currentFloorPlan, setCurrentFloorPlan] = useState<string | null>(null);
  const [currentFloorPlanSize, setCurrentFloorPlanSize] = useState<number | null>(null);
  const [currentFloorPlanSvg, setCurrentFloorPlanSvg] = useState<string | null>(null);
  const [currentFloorPlanFilename, setCurrentFloorPlanFilename] = useState<string | null>(null);
  const [currentFloorHighPriority, setCurrentFloorHighPriority] = useState<boolean | null>(null);
  const [floorNameError, setFloorNameError] = useState<string | null>(null);
  const [floorNumberError, setFloorNumberError] = useState<string | null>(null);
  const [floorExternalIdError, setFloorExternalIdError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [alertTitle, setAlertTitle] = useState<string>();
  const [alertMessage, setAlertMessage] = useState<string | ReactElement>();
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertAction, setAlertAction] = useState<() => void>(null);
  const [alertActionTitle, setAlertActionTitle] = useState<string>(null);
  const [addingFloor, setAddingFloor] = useState<boolean>(false);
  const [
    priorityDropdownShown, setPriorityDropdownShown,
  ] = useState<boolean>(false);

  const dispatch = useDispatch();

  const buildings: object = useSelector((state:
    { locations: { buildings } }) => state.locations.buildings);

  const showAlert = (
    title: string,
    message: string | ReactElement,
    error?: boolean,
    action?: () => void,
    actionTitle?: string,
  ) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertError(error);
    setAlertAction(() => action);
    setAlertActionTitle(actionTitle);
    setLoading(false);
    setAlertVisible(true);
  };

  useEffect(() => {
    if (isVisible) {
      setVisibilityToggleEnabled(false);
      const timeout = setTimeout(() => {
        setVisibilityToggleEnabled(true);
        clearTimeout(timeout);
      }, 500);
    } else setVisibilityToggleEnabled(true);
    setLoading(false);
    setAddingFloor(false);
    setFloorData(floors);
  }, [isVisible]);

  useEffect(() => {
    setFloorNameError(null);
    setFloorNumberError(null);
    setSelectedFloorId(floorToUpdate?.LocationId ?? selectedFloor?.LocationId);
    setCurrentFloorName(floorToUpdate?.Name ?? selectedFloor?.Name);
    setCurrentFloorNumber(floorToUpdate?.Number ?? selectedFloor?.Number);
    setCurrentFloorExternalId(floorToUpdate?.ExternalId ?? selectedFloor?.ExternalId);
    setCurrentFloorHighPriority(floorToUpdate?.IsHighPriority
      ?? selectedFloor?.IsHighPriority ?? false);
    setCurrentFloorPlan(floorToUpdate?.plan ?? selectedFloor?.plan);
    setCurrentFloorPlanSize(floorToUpdate?.imageSize ?? selectedFloor?.imageSize);
    setCurrentFloorPlanFilename(floorToUpdate?.planFilename ?? selectedFloor?.planFilename
      ?? selectedFloor?.planFilename ?? selectedFloor?.Svg);
    setCurrentFloorPlanSvg(floorToUpdate?.Svg ?? selectedFloor?.Svg);
  }, [selectedFloor, addingFloor, isVisible]);

  useEffect(() => setFloorData(floors), [floors]);

  const validateFloorName = (text: string) => {
    if (!(text?.trim().length > 0)) {
      setFloorNameError('Floor name cannot be empty');
      return false;
    }
    if (text?.trim().length > maxFloorNameLength) {
      setFloorNameError(`Floor name cannot exceed ${maxFloorNameLength} characters`);
      return false;
    }
    if (!nameRegex.exp.test(text)) {
      setFloorNameError(`Floor name${nameRegex.message}`);
      return false;
    }
    if (floors?.filter((floor: Floor) => text?.trim() === floor.Name?.trim()
      && floor.LocationId !== selectedFloorId).length > 0) {
      setFloorNameError('Another floor exists with the same name; floor names must be unique');
      return false;
    }
    setFloorNameError(null);
    return true;
  };

  const validateFloorNumber = (text: string) => {
    if (!(text?.trim().length > 0)) {
      setFloorNumberError('Floor number cannot be empty');
      return false;
    }
    if (text?.trim().length > maxFloorNumberLength) {
      setFloorNumberError(`Floor name cannot exceed ${maxFloorNumberLength} characters`);
      return false;
    }
    if (!nameRegex.exp.test(text)) {
      setFloorNumberError(`Floor number${nameRegex.message}`);
      return false;
    }
    if (floors?.filter((floor: Floor) => text?.trim() === floor.Number?.trim()
      && floor.LocationId !== selectedFloorId).length > 0) {
      setFloorNumberError('Another floor exists with the same number; floor numbers must be unique');
      return false;
    }
    setFloorNumberError(null);
    return true;
  };

  const validateFloorExternalId = (externalId: string) => {
    if (externalId?.trim().length > 0 && !externalIdRegex.exp.test(externalId.trim())) {
      setFloorExternalIdError(externalIdRegex.message);
      return false;
    }
    setFloorExternalIdError(null);
    return true;
  };

  const editFloorName = (text: string) => {
    validateFloorName(text);
    setCurrentFloorName(text);
  };

  const editFloorNumber = (text: string) => {
    validateFloorNumber(text);
    setCurrentFloorNumber(text);
  };

  const editFloorExternalId = (externalId: string) => {
    validateFloorExternalId(externalId);
    setCurrentFloorExternalId(externalId);
  };

  const closeModal = visibilityToggleEnabled ? hide : null;

  const cancel = () => {
    if (selectedFloor) setSelectedFloor(null);
    else if (addingFloor) setAddingFloor(false);
    else closeModal();
  };

  const removeFloorFromList = (LocationId: string) => {
    if (setFloors) {
      const updatedFloors: Floor[] = floors
        ?.filter((floor: Floor) => floor.LocationId !== LocationId)
        .sort((a: Floor, b: Floor) => a.Index - b.Index);
      setFloors(updatedFloors);
    }
    setSelectedFloor(null);
  };

  const floorPlanError = (errorText?: string) => showAlert(
    'Error',
    errorText ?? FLOOR_PLAN_UPDATE_FAILURE,
  );

  const updateFloorError = (add: boolean, errorText?: string) => showAlert(
    'Error',
    errorText?.toString()
    ?? `We are unable to ${add ? 'add' : 'edit'} the floor right now. Please try again later.`,
  );

  const deleteFloorError = (errorText?: string) => showAlert(
    'Error',
    errorText?.toString() ?? DELETE_FLOOR_FAILURE,
  );

  const removeFloor = (LocationId: string, Name: string) => {
    showAlert(
      `Delete ${Name}?`,
      <Text>
        {'Are you sure you want to delete the floor '}
        <Text style={styles.boldText}>{Name}</Text>
        {'?'}
      </Text>,
      true,
      () => {
        setLoading(true);
        deleteFloor(LocationId).then((res) => {
          if (res && !res.isError) {
            removeFloorFromList(LocationId);
            const updatedBuildingData = structuredClone(buildings);
            delete updatedBuildingData[ParentId].floors[LocationId];
            dispatch(updateLocations(updatedBuildingData));
            if (deleteFloorToUpdate) deleteFloorToUpdate();
            else {
              showAlert(
                `Deleted ${Name}`,
                <Text>
                  {'Floor '}
                  <Text style={styles.boldText}>{Name}</Text>
                  {' was successfully deleted'}
                </Text>,
                false,
              );
            }
          } else deleteFloorError(res?.errorText);
        }).catch((error) => deleteFloorError(error?.message));
      },
      'Delete',
    );
  };

  const save = () => {
    if (loading) return;
    setLoading(true);
    if (addingFloor || selectedFloorId || addFloor || floorToUpdate) {
      const valid: boolean = validateFloorName(currentFloorName)
        && validateFloorNumber(currentFloorNumber)
        && (validateFloorExternalId(currentFloorExternalId));
      if (valid) {
        updateFloor(
          currentFloorName?.trim(),
          currentFloorNumber?.trim(),
          currentFloorHighPriority,
          currentFloorPlan,
          currentFloorPlanFilename?.split('.').pop(),
          getFileNameFromPath(currentFloorPlanFilename),
          currentFloorPlanSize,
          ParentId,
          selectedFloorId ?? floorToUpdate?.LocationId,
          currentFloorExternalId?.trim(),
        ).then((res) => {
          if (res && !res?.isError) {
            const updatedFloor: Floor = {
              Name: currentFloorName?.trim(),
              Number: currentFloorNumber?.trim(),
              IsHighPriority: currentFloorHighPriority,
              imageSize: currentFloorPlanSize,
              planFilename: currentFloorPlanFilename?.trim(),
              LocationId: selectedFloorId ?? res?.LocationId?.LocationId,
              ParentId,
              Index: selectedFloor?.Index ?? (floorToUpdate?.Index ?? 0),
              Svg: selectedFloor?.Svg ?? floorToUpdate?.Svg ?? currentFloorPlan,
              ExternalId: currentFloorExternalId,
              Type: LocationType.FLOOR,
            };
            if (setFloors) {
              if (selectedFloorId || floorToUpdate) {
                setFloors(floors.map((floor: Floor) => ((floor.LocationId === selectedFloorId
                  || floorToUpdate?.LocationId === floor.LocationId) ? updatedFloor : floor))
                  .sort((a: Floor, b: Floor) => a.Index - b.Index));
              } else setFloors([...floors, updatedFloor]);
            } else if (editFloor) editFloor(updatedFloor);
            else if (addFloor) {
              addFloor({
                ...updatedFloor,
                Index: Math.max(...floors.map((f: Floor) => f.Index)) + 1,
              });
            }
            setAddingFloor(false);
            setSelectedFloor(null);
            setLoading(false);
            showAlert(
              `${selectedFloorId ? 'Edited' : 'Added'} ${currentFloorName?.trim()}`,
              <Text>
                {selectedFloorId ? 'Floor ' : 'A new floor '}
                <Text style={styles.boldText}>{currentFloorName?.trim()}</Text>
                {` has been ${selectedFloorId ? 'edited' : 'added'} successfully`}
              </Text>,
              false,
            );
            if (floorToUpdate || addFloor) hide();
          } else updateFloorError(!!selectedFloorId, res?.errorText);
        }).catch((error) => updateFloorError(!!selectedFloorId, error?.message));
      } else setLoading(false);
    } else {
      updateFloorOrder(floorData
        ?.map((floor: Floor, Index: number) => ({ Index, LocationId: floor.LocationId })))
        .then((res) => {
          if (res && !res.isError) {
            setFloors([...floorData]);
            showAlert(
              'Success',
              'Sequence of floors was successfully updated',
              false,
            );
            hide();
          } else floorPlanError(res?.errorText);
        }).catch((error) => floorPlanError(error?.message));
    }
  };

  return <>
    <Modal
      visible={isVisible}
      style={Style.modal}
      onRequestClose={hide}
      transparent={true}
    >
      <Pressable onPress={closeModal} style={{
        flex: 1,
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        cursor: 'auto',
      }}>
        <Pressable
          onPress={(e) => {
            e.stopPropagation();
            setPriorityDropdownShown(false);
          }}
          style={{
            alignSelf: 'center', justifyContent: 'center', cursor: 'auto', flex: 1, marginVertical: 20,
          }}
        >
          <View style={[styles.container, Style.modalContent]}>
            <Text style={styles.title}>
              {(selectedFloorId || addingFloor || floorToUpdate)
                ? `${addingFloor ? 'Add' : 'Edit'} floor` : 'Set floors'}
            </Text>
            {(selectedFloorId || addingFloor || floorToUpdate || addFloor)
              ? <FloorDetailsForm
                {...{
                  currentFloorName,
                  currentFloorNumber,
                  currentFloorExternalId,
                  currentFloorHighPriority,
                  currentFloorPlan,
                  currentFloorPlanFilename,
                  currentFloorPlanSize,
                  currentFloorPlanSvg,
                  priorityDropdownShown,
                  editFloorName,
                  editFloorNumber,
                  editFloorExternalId,
                  setCurrentFloorPlan,
                  setCurrentFloorPlanFilename,
                  setCurrentFloorPlanSize,
                  setCurrentFloorHighPriority,
                  setPriorityDropdownShown,
                  floorNameError,
                  floorNumberError,
                  floorExternalIdError,
                }}
                originalFloorExternalId={floorToUpdate?.ExternalId ?? selectedFloor?.ExternalId}
              /> : <FloorList
                addFloor={() => setAddingFloor(true)}
                floors={floorData}
                setData={(updatedFloors: Floor[]) => setFloorData(updatedFloors)}
                edit={setSelectedFloor}
                remove={(LocationId: string, Name: string) => removeFloor(LocationId, Name)}
              />}
            <View style={[
              Style.row, styles.buttonRow, !(selectedFloorId) && styles.mainButtonsRow,
            ]}>
              {selectedFloorId && canDeleteLocations && <PressableStyled
                style={styles.deleteIcon}
                onPress={() => removeFloor(
                  selectedFloorId,
                  selectedFloor?.Name ?? floorToUpdate?.Name,
                )}
              >
                <BinIcon24 color={styles.deleteIcon.borderColor} />
              </PressableStyled>}
              <View style={Style.row}>
                <Button
                  affirmative={false}
                  onPress={cancel}
                  title='Cancel'
                  testID='cancel-btn'
                />
                <Button
                  affirmative
                  onPress={save}
                  disabled={loading || !!floorNameError || !!floorNumberError}
                  title='Save'
                  testID='save-btn'
                />
              </View>
            </View>
            <PressableStyled
              style={Style.closeButton}
              onPress={hide}
              testID={testID ?? 'close-add-floor-menu-btn'}
            >
              <CloseModalIcon />
            </PressableStyled>
            <ActivityIndicator
              style={Style.activityIndicator}
              animating={loading}
              size='large'
            />
          </View>
        </Pressable>
      </Pressable>
    </Modal>
    <AlertModal
      modalVisible={alertVisible}
      setModalVisible={setAlertVisible}
      title={alertTitle}
      message={alertMessage}
      error={alertError}
      action={alertAction}
      actionTitle={alertActionTitle}
    />
  </>;
};

const styles = StyleSheet.create({
  container: {
    width: isMobile ? '80%' : 560,
    flex: 1,
    justifyContent: 'space-between',
    alignSelf: 'center',
    paddingTop: 60,
    paddingBottom: 40,
    alignItems: 'center',
  },
  title: {
    fontSize: 28,
    lineHeight: 42,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
    marginBottom: 8,
  },
  buttonRow: {
    marginTop: 40,
    alignSelf: 'stretch',
    marginHorizontal: 30,
  },
  deleteIcon: {
    height: 40,
    aspectRatio: 1,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 4,
    borderColor: Colors.light.hereworksRed700,
    shadowColor: '#323247',
    shadowRadius: 2,
    elevation: 2,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
  },
  mainButtonsRow: {
    justifyContent: 'center',
  },
  stretch: {
    alignSelf: 'stretch',
  },
  boldText: { fontFamily: 'Poppins_700Bold' },
});

export default SetFloorsModal;
